import { DateTime } from "luxon";
import { formatDate } from "swing-components";

import { translate } from "~utils";

export const ActivityTypes = {
  SUB_REQ_CREATED: "event.type/sub-req-created",
  EDIT_REQUEST: "event.type/edit-request",
  SUB_CANCELED: "event.type/sub-canceled",
  SUB_LEFT_EARLY_APPROVED: "event.type/sub-left-early-approved",
  SUB_LEFT_EARLY_APPROVED_DELETED: "event.type/sub-left-early-approved-deleted",
  SUB_REQ_CANCELED: "event.type/sub-req-canceled",
  SUB_ADDED_TO_POOL: "event.type/sub-added-to-pool",
  SUB_REMOVED_FROM_POOL: "event.type/sub-removed-from-pool",
  SUB_CHOSEN: "event.type/sub-chosen",
  SUB_NOTIFIED: "event.type/sub-notified",
  EDIT_FILLED_REQUEST: "event.type/edit-filled-request",
  NO_SHOW_UNCONFIRMED: "event.type/no-show-unconfirmed",
  NO_SHOW_CONFIRMED: "event.type/no-show-confirmed",
  NO_SHOW_APPROVED: "event.type/no-show-approved",
  NO_SHOW_DELETED: "event.type/no-show-deleted",
  FEEDBACK_ADDED: "event.type/feedback-added",
  RECENT_SUB_CHOSEN: "event.type/recent-sub-chosen",
  LATE: "event.type/late",
  LATE_DELETED: "event.type/late-deleted",
  LUNCH_LATE: "event.type/lunch-late",
  LUNCH_LATE_DELETED: "event.type/lunch-late-deleted",
  SUB_LEFT_EARLY: "event.type/sub-left-early",
  SUB_LEFT_EARLY_DELETED: "event.type/sub-left-early-deleted",
  REQUEST_START_DATE_ADJUSTED: "event.type/request-start-date-adjusted",
  TRIM_LONG_TERM_REQUEST: "event.type/trim-long-term-request",
  URGENT_ON_CAMPUS: "event.type/urgent-on-campus",
  URGENT_ON_CAMPUS_DELETED: "event.type/urgent-on-campus-deleted",
  RELEASED_TO_GENERAL_POOL: "event.type/released-to-general-pool",
} as const;

type ActivityAuthor = {
  id?: string;
  email?: string;
  name?: string;
};

type ActivitySub = {
  id?: string;
  name?: string;
};

type ActivityKeyType = (typeof ActivityTypes)[keyof typeof ActivityTypes];

export type ActivityBase = {
  timestamp: string;
  type: ActivityKeyType;
  author?: ActivityAuthor;
  sub?: ActivitySub;
  start?: string;
} & Record<string, unknown>;

export class LogEvent {
  private event: ActivityBase;
  private timeZone?: string;
  private _formattedDate: string;
  private _start: DateTime;

  constructor(event: ActivityBase, schoolTimeZone?: string) {
    this.event = event;
    this.timeZone = schoolTimeZone ?? DateTime.local().zoneName;

    this._formattedDate =
      formatDate({
        date: DateTime.fromISO(this.event.timestamp).setZone(this.timeZone),
        format: "Wed, Jan 1 at 3:30 pm",
      }) ?? "";

    this._start = DateTime.fromISO(this.event?.start ?? "").setZone(this.timeZone);
  }

  get dateString() {
    return this._formattedDate;
  }

  get timestamp() {
    return this.event?.timestamp ?? "";
  }

  get action() {
    return this.event?.type;
  }

  get userName() {
    return this.event?.author?.name || this.event?.author?.email;
  }

  get subName() {
    return this.event?.sub?.name || "";
  }

  get subId() {
    return this.event?.sub?.id;
  }

  get displayedActivityString(): string {
    const authorName = this.event?.author?.name || this.event?.author?.email || "";
    const subName = this.event?.sub?.name || "";
    const date = this._formattedDate;

    switch (this.event.type) {
      case ActivityTypes.SUB_REQ_CREATED:
        return translate("EVENT_LOG_LABEL_SUB_REQ_CREATED", authorName);
      case ActivityTypes.EDIT_REQUEST:
        return translate("EVENT_LOG_LABEL_EDIT_REQUEST");
      case ActivityTypes.SUB_REQ_CANCELED:
        return translate("EVENT_LOG_LABEL_SUB_REQ_CANCELED", authorName);
      case ActivityTypes.SUB_LEFT_EARLY_APPROVED:
        return translate("EVENT_LOG_LABEL_SUB_LEFT_EARLY_APPROVED");
      case ActivityTypes.SUB_LEFT_EARLY_APPROVED_DELETED:
        return translate("EVENT_LOG_LABEL_SUB_LEFT_EARLY_APPROVED_DELETED");
      case ActivityTypes.SUB_ADDED_TO_POOL:
        return translate("EVENT_LOG_LABEL_SUB_ADDED_TO_POOL", subName);
      case ActivityTypes.SUB_CHOSEN:
        return translate("EVENT_LOG_LABEL_SUB_CHOSEN", { authorName, subName });
      case ActivityTypes.SUB_REMOVED_FROM_POOL:
        return translate("EVENT_LOG_LABEL_SUB_REMOVED_FROM_POOL", subName);
      case ActivityTypes.SUB_CANCELED:
        return translate("EVENT_LOG_LABEL_SUB_CANCELED", subName);
      case ActivityTypes.SUB_NOTIFIED:
        return translate("EVENT_LOG_LABEL_SUB_NOTIFIED");
      case ActivityTypes.EDIT_FILLED_REQUEST:
        return translate("EVENT_LOG_LABEL_EDIT_FILLED_REQUEST", authorName);
      case ActivityTypes.FEEDBACK_ADDED:
        return translate("EVENT_LOG_LABEL_FEEDBACK_ADDED", authorName);
      case ActivityTypes.REQUEST_START_DATE_ADJUSTED:
        return translate("EVENT_LOG_LABEL_REQUEST_START_DATE_ADJUSTED", {
          startDate: DateTime.fromISO(this.event?.start ?? "").setZone(this.timeZone),
        });
      case ActivityTypes.URGENT_ON_CAMPUS:
        return translate("EVENT_LOG_LABEL_URGENT_CONCERN");
      case ActivityTypes.RELEASED_TO_GENERAL_POOL:
        return translate("EVENT_LOG_LABEL_RELEASED_TO_GENERAL_POOL", authorName);

      // Everything below this line is no longer likely to appear.
      // Probably okay to delete with a future tech wealth ticket.

      // None of these issues are tracked as events anymore. Instead,
      // they're tracked as :interval/issue events. A future datomic
      // index access to find if there are any active requests with these
      // events should confirm that they no longer exist, and then they
      // can be deleted. I will still be adding them for backwards compatibility
      // until the above query gets run across prod.

      // I'll also not be adding them to the string table.

      case ActivityTypes.NO_SHOW_UNCONFIRMED:
        return `Admin ${authorName} confirmed ${subName} as a tentative no-show on ${this._formattedDate}`;
      case ActivityTypes.NO_SHOW_CONFIRMED:
        return `Admin ${authorName} confirmed ${subName} as a no-show`;
      case ActivityTypes.NO_SHOW_APPROVED:
        return translate("EVENT_LOG_LABEL_NO_SHOW_APPROVED", { date: this._start });
      case ActivityTypes.NO_SHOW_DELETED:
        return `Admin ${authorName} deleted no-show report for ${this._formattedDate}`;
      case ActivityTypes.LATE:
        return `Admin ${authorName} reported Sub ${subName} as late on ${date}`;
      case ActivityTypes.LATE_DELETED:
        return `Admin ${authorName} deleted late sub arrival reported for ${date}`;
      case ActivityTypes.LUNCH_LATE:
        return `Admin ${authorName} reported Sub ${subName} as arriving late for lunch on ${date}`;
      case ActivityTypes.LUNCH_LATE_DELETED:
        return `Admin ${authorName} deleted late lunch reported for ${date}`;
      case ActivityTypes.SUB_LEFT_EARLY:
        return `Admin ${authorName} reported Sub ${subName} as leaving early on ${date}`;
      case ActivityTypes.SUB_LEFT_EARLY_DELETED:
        return `Admin ${authorName} deleted early departure report for sub ${subName}`;
      // This is also deprecated and a cljs-only flag. Still adding for compatibility.
      case ActivityTypes.RECENT_SUB_CHOSEN:
        return `Admin chose a sub recently.`;
      case ActivityTypes.URGENT_ON_CAMPUS_DELETED:
        return `On-campus concern log deleted`;

      default:
        // eslint-disable-next-line no-console
        console.error(
          `The event type ${this.event.type} is being sent from the backend but has not been registered on this page yet.`,
        );
        // eslint-disable-next-line no-console
        console.error(this.event.type);
        return this.event.type || "";
    }
  }
}
